import { useState } from "react";
import { Avatar, Dialog } from "../components";
import { GameLayout } from "./shared/GameLayout";
import { GameIcon } from "./shared/GameIcons";
import { PrivateTrigger } from "./shared/PrivatePanel";
import {
  GameArt,
  PrivateInfo,
  TargetAction,
  FinishedRoles,
  PersonalOutcomeLine,
  RoleGrid,
  SubmitButton,
  draftScope,
} from "./shared/PresenterParts";
import { useDraft } from "./shared/drafts";
import { personalOutcome } from "./outcome";
import { offlineSeatIds, publicTableModel } from "./public-presentation";
import { playerName, type PlayProps } from "./types";
import {
  fascistSlotEffects,
  powerHelp,
  powerLabels,
} from "./secret-hitler-board";
export { fascistSlotEffects } from "./secret-hitler-board";
function PolicyHand({ props }: { props: PlayProps }) {
  const { state, send } = props,
    g = state.game,
    [selected, setSelected] = useDraft<number | null>(
      draftScope(state),
      "legislate:index",
      null,
    );
  return (
    <>
      <p className="private-instruction">
        {g.phase === "president-discard"
          ? "Discard one policy. The other two go to the chancellor."
          : "Choose one policy to enact."}
      </p>
      <div
        className="play-policy-hand"
        role="group"
        aria-label="Your policies"
        data-tour="policy-hand"
      >
        {(g.private.hand ?? []).map((kind: string, i: number) => (
          <button
            type="button"
            key={i}
            className={`play-policy-card ${kind}`}
            aria-pressed={selected === i}
            onClick={() => setSelected(i)}
          >
            <span className="policy-card-face" aria-hidden="true">
              <GameIcon name={kind} size={30} />
            </span>
            <strong>{kind}</strong>
          </button>
        ))}
      </div>
      <SubmitButton
        type="button"
        disabled={selected === null}
        onClick={() =>
          send({
            type: "legislate",
            [g.phase === "president-discard" ? "discardIndex" : "enactIndex"]:
              selected,
          })
        }
      >
        {selected === null
          ? g.phase === "president-discard"
            ? "Pick a policy to discard"
            : "Pick a policy to enact"
          : g.phase === "president-discard"
            ? "Discard selected policy"
            : "Enact selected policy"}
      </SubmitButton>
      {g.legalActions.includes("requestVeto") ? (
        <button
          className="secondary full"
          onClick={() => send({ type: "requestVeto" })}
        >
          Request a veto
        </button>
      ) : null}
    </>
  );
}
export function SecretHitler(props: PlayProps) {
  const { state, send, isDisplay } = props,
    g = state.game,
    p = g.public,
    s = g.private,
    players = p.players ?? [],
    n = (id: string) => playerName(players, id),
    can = (a: string) =>
      !isDisplay && !state.isSpectator && g.legalActions.includes(a);
  const scope = draftScope(state),
    publicAudience = isDisplay || state.isSpectator,
    viewerId = publicAudience ? null : state.playerId,
    offline = offlineSeatIds(state.players);
  const [vote, setVote] = useDraft<boolean | null>(scope, "vote:approve", null),
    [help, setHelp] = useState<string | null>(null);
  const model = publicTableModel(g, state.players ?? []);
  const effects = fascistSlotEffects(p.startingPlayerCount ?? players.length);
  const last = p.policyHistory?.at(-1),
    winner = g.winner as { reason?: string; team?: string } | undefined;
  const titleFor = (can: (a: string) => boolean) =>
    g.finished
    ? winner?.team === "liberal"
      ? "Liberal victory."
      : "Fascist victory."
    : can("nominate")
      ? "Choose a chancellor."
      : g.phase === "nomination"
        ? `${n(p.presidentId)} is choosing a chancellor.`
        : g.phase === "election"
          ? "Elect this government?"
          : g.phase === "election-result"
            ? `Government ${p.lastElection?.approved ? "elected." : "rejected."}`
            : g.phase === "president-discard"
              ? can("legislate")
                ? "Discard one policy."
                : "The president is choosing."
              : g.phase === "chancellor-enact"
                ? can("legislate")
                  ? "Enact one policy."
                  : "The chancellor is choosing."
                : g.phase === "veto-response"
                  ? can("respondVeto")
                    ? "Accept the veto?"
                    : "Veto requested."
                  : g.phase === "policy-result"
                    ? `${last?.policy === "liberal" ? "Liberal" : "Fascist"} policy enacted.`
                    : g.phase === "executive"
                      ? can("executive")
                        ? p.executivePower === "execute"
                          ? "Choose who to execute."
                          : p.executivePower === "investigate"
                            ? "Investigate a player."
                            : p.executivePower === "special-election"
                              ? "Pick the next president."
                              : (powerLabels[p.executivePower] ??
                                  "Executive power") + "."
                        : (powerLabels[p.executivePower] ?? "Executive power") +
                          "."
                      : "Executive action complete.";
  const title = titleFor(can);
  // Public headline: the same copy with no seat's legal actions behind it.
  const publicHeadline =
    g.phase === "nomination"
      ? `${n(p.presidentId)} is nominating.`
      : titleFor(() => false);
  const action = can("nominate") ? (
    <TargetAction
      players={players}
      ids={p.eligibleChancellorIds ?? []}
      type="nominate"
      label="Nominate chancellor"
      emptyLabel="Pick a chancellor"
      send={send}
      scope={scope}
      offlineIds={offline}
    />
  ) : can("vote") ? (
    <>
      <div
        className="play-ballots"
        role="group"
        aria-label="Your ballot"
        data-tour="ballot"
      >
        {([true, false] as const).map((v) => (
          <button
            type="button"
            key={String(v)}
            className={v ? "is-ja" : "is-nein"}
            aria-pressed={vote === v}
            onClick={() => setVote(v)}
          >
            <strong>{v ? "Ja!" : "Nein!"}</strong>
            <span>{v ? "Approve" : "Reject"}</span>
          </button>
        ))}
      </div>
      <SubmitButton
        type="button"
        disabled={vote === null}
        onClick={() => send({ type: "vote", approve: vote })}
      >
        {vote === null ? "Pick Ja! or Nein!" : "Submit ballot"}
      </SubmitButton>
    </>
  ) : can("legislate") ? (
    <PrivateTrigger>Open your policy hand</PrivateTrigger>
  ) : can("respondVeto") ? (
    <>
      <SubmitButton
        type="button"
        onClick={() => send({ type: "respondVeto", approve: true })}
      >
        Agree to veto
      </SubmitButton>
      <button
        className="secondary full"
        onClick={() => send({ type: "respondVeto", approve: false })}
      >
        Decline veto
      </button>
    </>
  ) : can("executive") ? (
    <TargetAction
      players={players}
      ids={s.eligibleTargetIds ?? []}
      type="executive"
      label={
        p.executivePower === "execute"
          ? "Execute player"
          : p.executivePower === "investigate"
            ? "Investigate player"
            : "Choose next president"
      }
      emptyLabel={
        p.executivePower === "execute"
          ? "Pick who to execute"
          : p.executivePower === "investigate"
            ? "Pick who to investigate"
            : "Pick the next president"
      }
      send={send}
      scope={scope}
      offlineIds={offline}
      confirm={p.executivePower === "execute"}
    />
  ) : null;
  const distinctPowers = [
    ...new Set(
      effects.filter(
        (e): e is Exclude<typeof e, null | "victory"> =>
          Boolean(e) && e !== "victory",
      ),
    ),
  ];
  const board = (
    <>
      {g.finished ? (
        <PersonalOutcomeLine
          outcome={personalOutcome(g, state.playerId, publicAudience)}
        />
      ) : null}
      <div className="sh-board" data-tour="policy-tracks">
        {(["liberal", "fascist"] as const).map((kind) => {
          const count = p[kind + "Policies"] ?? 0,
            total = kind === "liberal" ? 5 : 6;
          return (
            <section
              className={`sh-track ${kind}`}
              data-tour={`track-${kind}`}
              key={kind}
              aria-label={`${kind} policy track`}
            >
              <div className="sh-track-label">
                <GameIcon name={kind} size={22} />
                <strong>{kind === "liberal" ? "Liberal" : "Fascist"}</strong>
                <span className="sh-count">
                  {count}
                  <small> / {total}</small>
                </span>
              </div>
              <ol>
                {Array.from({ length: total }, (_, i) => {
                  const enacted = i < count,
                    effect =
                      kind === "fascist"
                        ? effects[i]
                        : i === 4
                          ? "victory"
                          : null;
                  return (
                    <li
                      data-policy-slot={kind}
                      key={i}
                      className={`${enacted ? "enacted" : ""}${effect ? " has-power" : ""}${!enacted && i === count ? " is-next" : ""}`}
                      aria-label={`${kind} policy ${i + 1}${enacted ? ", enacted" : ""}`}
                    >
                      <span className="sh-slot-number">{i + 1}</span>
                      {effect ? (
                        <button
                          type="button"
                          aria-label={`${powerLabels[effect]} at ${kind} policy ${i + 1}`}
                          onClick={() =>
                            setHelp(
                              kind === "liberal" ? "liberal-victory" : effect,
                            )
                          }
                        >
                          <GameIcon name={enacted ? kind : effect} size={22} />
                          <small>{powerLabels[effect]}</small>
                        </button>
                      ) : enacted ? (
                        <GameIcon name={kind} size={24} />
                      ) : null}
                    </li>
                  );
                })}
              </ol>
              {kind === "fascist" && distinctPowers.length ? (
                <ul
                  className="sh-power-legend"
                  aria-hidden="true"
                  data-tour="powers"
                >
                  {distinctPowers.map((e) => (
                    <li key={e}>
                      <GameIcon name={e} size={14} />
                      {powerLabels[e]}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          );
        })}
        <div className="sh-board-foot">
          <div
            className="sh-failures"
            data-tour="election-tracker"
            role="group"
            aria-label={`Failed elections: ${p.electionTracker ?? 0} of 3`}
          >
            <span>Failed elections</span>
            <span className="sh-failure-marks">
              {[1, 2, 3].map((v) => (
                <i
                  data-failure-marker={v}
                  key={v}
                  className={v <= (p.electionTracker ?? 0) ? "filled" : ""}
                />
              ))}
            </span>
            <small>3rd → top policy</small>
          </div>
          <div className="sh-thresholds" data-tour="thresholds">
            <span>After 3 Fascist: electing Hitler wins</span>
            <button type="button" onClick={() => setHelp("veto")}>
              <GameIcon name="veto" size={16} /> Veto at 5
            </button>
          </div>
        </div>
      </div>
      {g.finished ? null : (
      <div className="sh-offices" data-tour="offices">
        {[
          ["President", p.presidentId],
          [
            p.chancellorId ? "Chancellor" : "Chancellor nominee",
            p.chancellorId ?? p.chancellorCandidateId,
          ],
        ].map(([office, id]) => (
          <div
            key={office}
            className={`${id ? "" : "is-vacant"}${id && offline.includes(id) ? " is-offline" : ""}`}
          >
            <span className="seat-avatar">
              <Avatar
                name={players.find((v: any) => v.id === id)?.name ?? "—"}
                index={Math.max(
                  0,
                  players.findIndex((v: any) => v.id === id),
                )}
              />
              {id && offline.includes(id) ? (
                <i className="seat-offline-dot" aria-hidden="true" />
              ) : null}
            </span>
            <span>
              <small>
                {office}
                {id && offline.includes(id) ? " · offline" : ""}
              </small>
              <strong>{id ? n(id) : "Not chosen yet"}</strong>
            </span>
          </div>
        ))}
      </div>
      )}
      {g.phase === "policy-result" && last?.chaos ? (
        <p className="play-board-caption">
          Election tracker policy · no executive power
        </p>
      ) : null}
      {g.phase === "election-result" && p.lastElection ? (
        <ul
          className="sh-election-result"
          aria-label="Ballots"
          data-tour="election-result"
        >
          {p.lastElection.votes.map((v: any) => (
            <li key={v.playerId} className={v.approve ? "is-ja" : "is-nein"}>
              <GameIcon name={v.approve ? "check" : "close"} size={14} />
              {n(v.playerId)}
              <span className="sr-only">{v.approve ? " Ja" : " Nein"}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <FinishedRoles state={state} />
      {g.finished ? <RoleGrid state={state} viewerId={viewerId} /> : null}
      {help ? (
        <Dialog
          title={
            help === "veto"
              ? "Veto power"
              : help === "liberal-victory"
                ? "Liberal victory"
                : powerLabels[help]
          }
          onClose={() => setHelp(null)}
        >
          <p className="play-help-copy">
            {help === "liberal-victory"
              ? "Enacting five Liberal policies immediately wins for the Liberal team."
              : powerHelp[help]}
          </p>
        </Dialog>
      ) : null}
    </>
  );
  return (
    <GameLayout
      {...props}
      title={title}
      contextArt={<GameArt kind="secret-hitler" variant="stamp" />}
      phaseLabel={g.finished ? "Result" : `Round ${g.round}`}
      phaseNote={
        g.phase === "election" && model.progress
          ? `${model.progress.submitted} of ${model.progress.eligible} voted`
          : null
      }
      publicTitle={publicHeadline}
      privateScope="match"
      progressLabel="voted"
      publicDescription={
        g.finished
          ? winner?.reason
          : g.phase === "executive-result" && p.executivePower === "execute"
            ? `${n(p.publicActions?.at(-1)?.targetId)} was eliminated.`
            : null
      }
      description={
        g.finished
          ? winner?.reason
          : g.phase === "nomination"
            ? null
            : g.phase === "veto-response" && can("respondVeto")
              ? "The chancellor wants to discard both policies. Agreeing advances the election tracker."
              : g.phase === "election"
                ? null
                : g.phase === "executive"
                  ? can("executive")
                    ? null
                    : powerHelp[p.executivePower]
                  : g.phase === "policy-result"
                    ? last?.chaos
                      ? "Election tracker policy. No executive power."
                      : p.executivePower
                        ? `Next: ${powerLabels[p.executivePower].toLowerCase()}.`
                        : null
                    : g.phase === "executive-result"
                      ? p.executivePower === "execute"
                        ? `${n(p.publicActions?.at(-1)?.targetId)} was eliminated.`
                        : !isDisplay &&
                            !state.isSpectator &&
                            state.playerId === p.presidentId
                          ? "Your private notes are in Your card."
                          : "The president can review their private notes in Your card."
                      : null
      }
      board={board}
      action={action}
      privateContent={<PrivateInfo state={state} />}
      privateAction={
        can("legislate") ? <PolicyHand props={props} /> : undefined
      }
    />
  );
}
